<template>
  <div class="consulta-virtual-page">
    <!-- Hero Header -->
    <header class="virtual-hero bg-primary-dark-base text-white py-5 px-3">
      <div class="container text-center py-4">
        <span
          class="badge badge-light text-primary-base px-3 py-2
            text-uppercase font-weight-bold mb-3 rounded-pill"
        >
          <font-awesome-icon
            icon="video"
            class="mr-2"
          />
          Teleconsulta AMII
        </span>
        <h1 class="h2 font-weight-bold mb-2">
          Consultas Médicas Virtuales
        </h1>
        <p
          class="lead text-white-50 mx-auto mb-0"
          style="max-width: 650px;"
        >
          Encuentra especialistas médicos disponibles en línea en este
          momento y agenda tu teleconsulta directamente sin salir de casa.
        </p>
      </div>
    </header>

    <main class="py-5 bg-light">
      <div class="container">
        <!-- Aviso de Veracidad y Transparencia -->
        <AvisoVeracidadEspecialidad class="mb-4" />

        <!-- Card del Buscador -->
        <div class="card shadow-sm p-4 rounded-xl bg-white text-dark mb-4">
          <div class="row align-items-center">
            <!-- 1. Especialidad Base -->
            <div class="col-12 col-md-5 my-2">
              <div class="md-form md-outline outline-select m-0">
                <select
                  id="searchBaseSpecialty"
                  v-model="filters.baseSpecialty"
                  class="custom-select"
                  @change="onSearchChange"
                >
                  <option value="">
                    Todas las especialidades
                  </option>
                  <option
                    v-for="spec in tags"
                    :key="spec.name"
                    :value="spec.name"
                  >
                    {{ spec.name }}
                  </option>
                </select>
                <label
                  for="searchBaseSpecialty"
                  class="label-active"
                >
                  Especialidad Base
                </label>
              </div>
            </div>

            <!-- 2. Etiqueta personalizada / Patología / Síntoma -->
            <div class="col-12 col-md-5 my-2">
              <mdb-input
                v-model="filters.customTag"
                label="Etiqueta, patología o síntoma"
                class="m-0"
                outline
                @input="onSearchChange"
              />
            </div>

            <!-- Botón Limpiar -->
            <div class="col-12 col-md-2 my-2 text-center">
              <mdb-btn
                type="button"
                flat
                dark-waves
                class="p-2 z-depth-0 w-100 m-0 text-muted"
                size="sm"
                @click="limpiarFiltros"
              >
                <font-awesome-icon
                  icon="broom"
                  class="mr-1"
                />
                Limpiar
              </mdb-btn>
            </div>
          </div>

          <!-- Switch: Disponibles en este momento -->
          <div
            class="row align-items-center justify-content-between
              mt-3 pt-3 border-top"
          >
            <div class="col-12 col-sm-auto mb-2 mb-sm-0">
              <label
                class="cursor-pointer small font-weight-bold text-dark-blue
                  d-inline-flex align-items-center mb-0"
              >
                <input
                  v-model="filters.onlyAvailableNow"
                  type="checkbox"
                  class="mr-2"
                  @change="onSearchChange"
                >
                <span class="mr-2">🟢</span>
                Mostrar únicamente médicos disponibles en este momento
              </label>
            </div>
            <div class="col-12 col-sm-auto text-sm-right">
              <small class="text-muted">
                {{ doctors.length }}
                especialista{{ doctors.length === 1 ? '' : 's' }}
                encontrado{{ doctors.length === 1 ? '' : 's' }}
              </small>
            </div>
          </div>
        </div>

        <!-- Resultados -->
        <section aria-label="Resultados de médicos virtuales">
          <!-- Loading state -->
          <div
            v-if="isLoading && !doctors.length"
            class="text-center py-5 text-muted"
          >
            <font-awesome-icon
              icon="circle-notch"
              size="2x"
              class="fa-spin text-primary-base mb-3"
            />
            <p class="small text-uppercase font-weight-bold">
              Consultando especialistas disponibles...
            </p>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="!isLoading && !doctors.length"
            class="card border-0 shadow-sm p-5 text-center bg-white rounded-xl"
          >
            <font-awesome-icon
              icon="calendar-xmark"
              size="3x"
              class="text-muted-light mb-3"
            />
            <h2 class="h5 font-weight-bold text-dark-blue">
              Sin especialistas disponibles con estos criterios
            </h2>
            <p
              class="text-muted small mx-auto mb-3"
              style="max-width: 500px;"
            >
              No encontramos médicos virtuales activos para la búsqueda
              actual. Puedes desactivar el filtro de atención inmediata
              o consultar otras especialidades.
            </p>
            <div>
              <mdb-btn
                v-if="filters.onlyAvailableNow"
                color="primary-base"
                size="sm"
                rounded
                @click="filters.onlyAvailableNow = false; onSearchChange();"
              >
                Ver todos los médicos con consulta virtual
              </mdb-btn>
            </div>
          </div>

          <!-- Grid de Doctores -->
          <div
            v-else
            class="row"
          >
            <div
              v-for="doc in doctors"
              :key="doc.id"
              class="col-12 col-md-6 col-lg-4 mb-4"
            >
              <article
                class="card h-100 border-0 shadow-sm rounded-xl p-3
                  d-flex flex-column justify-content-between
                  doctor-virtual-card"
              >
                <div>
                  <!-- Encabezado de la tarjeta -->
                  <div class="d-flex align-items-center mb-3">
                    <img
                      :src="getDoctorImage(doc)"
                      :alt="`Foto de ${doc.fname} ${doc.lname}`"
                      class="rounded-circle mr-3 doctor-avatar flex-shrink-0"
                    >
                    <div class="overflow-hidden">
                      <header
                        class="h6 font-weight-bold text-dark-blue mb-0
                          text-truncate"
                      >
                        {{ formatDoctorName(doc) }}
                      </header>
                      <small
                        v-if="doc.subname"
                        class="text-muted d-block text-truncate"
                      >
                        {{ doc.subname }}
                      </small>
                      <span
                        v-if="doc.primarySpecialty"
                        class="badge badge-primary-light text-primary-base
                          font-weight-bold mt-1 text-truncate d-inline-block"
                        style="max-width: 100%;"
                      >
                        {{ doc.primarySpecialty }}
                      </span>
                    </div>
                  </div>

                  <!-- Calificación y Opiniones -->
                  <div class="d-flex align-items-center mb-2 small">
                    <div
                      v-if="doc.ratingAvg > 0"
                      class="d-flex align-items-center text-warning mr-2"
                    >
                      <font-awesome-icon
                        icon="star"
                        class="mr-1"
                      />
                      <span class="font-weight-bold text-dark">
                        {{ doc.ratingAvg }}
                      </span>
                    </div>
                    <span
                      v-if="doc.ratingCount > 0"
                      class="text-muted"
                    >
                      ({{ doc.ratingCount }} valoraciones)
                    </span>
                    <span
                      v-else
                      class="text-muted font-italic"
                    >
                      Nuevo en AMII
                    </span>
                  </div>

                  <!-- Estado de Disponibilidad en el Momento -->
                  <div class="mb-3">
                    <span
                      v-if="doc.isOnlineNow"
                      class="badge badge-success py-1 px-2 font-weight-normal"
                    >
                      <span class="mr-1">●</span>
                      En turno ahora
                    </span>
                    <span
                      v-else-if="doc.nextSchedule"
                      class="badge badge-light border text-muted py-1 px-2"
                    >
                      <font-awesome-icon
                        icon="clock"
                        class="mr-1"
                      />
                      Turno: {{ doc.nextSchedule }}
                    </span>
                    <span
                      v-else
                      class="badge badge-light border text-muted py-1 px-2"
                    >
                      Consulta virtual programada
                    </span>
                  </div>

                  <!-- Etiquetas personalizadas / Patologías -->
                  <div
                    v-if="doc.customTags && doc.customTags.length"
                    class="d-flex flex-wrap mb-3"
                  >
                    <span
                      v-for="(ct, idx) in doc.customTags.slice(0, 4)"
                      :key="idx"
                      class="badge badge-light text-muted mr-1 mb-1 border
                        small font-weight-normal py-1 px-2"
                    >
                      {{ ct }}
                    </span>
                  </div>
                </div>

                <!-- Botones y Enlace a Perfil Público -->
                <footer class="pt-3 border-top mt-auto profile-footer">
                  <div class="mb-2 text-center profile-link-wrapper">
                    <a
                      v-if="doc.profileUrl"
                      :href="`/_/${doc.profileUrl}`"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="small font-weight-bold ver-perfil-link"
                    >
                      Ver perfil público y credenciales &rarr;
                    </a>
                  </div>
                  <mdb-btn
                    color="primary-base"
                    size="sm"
                    rounded
                    block
                    class="m-0 py-2 font-weight-bold shadow-none"
                    @click="agendar(doc)"
                  >
                    <font-awesome-icon
                      icon="video"
                      class="mr-1"
                    />
                    Agendar Teleconsulta
                  </mdb-btn>
                </footer>
              </article>
            </div>
          </div>

          <!-- Botón Cargar Más Paginado -->
          <div
            v-if="doctors.length >= pageSize * page && !isLoading"
            class="text-center mt-4"
          >
            <mdb-btn
              color="primary-base"
              outline="primary-base"
              size="sm"
              rounded
              class="px-4"
              @click="cargarMas"
            >
              Cargar más especialistas
            </mdb-btn>
          </div>
        </section>
      </div>
    </main>

    <!-- Modal Agendar Cita -->
    <modalAgendarCita
      :show-modal="showAgendarCita"
      :doctor="doctorSeleccionado"
      @close="showAgendarCita = false"
    />
  </div>
</template>

<script>
import gql from 'graphql-tag';
import {mdbBtn, mdbInput} from 'mdbvue';
import AvisoVeracidadEspecialidad
from '@/components/AvisoVeracidadEspecialidad.vue';
import modalAgendarCita from '@/components/modalAgendarCita.vue';
import especialidadesGql from '@/graphql/especialidades.gql';
import activeVirtualDoctorsGql from '@/graphql/activeVirtualDoctors.gql';

const imgPath = process.env.NODE_ENV === 'production'
  ? process.env.VUE_APP_MULTIMEDIA_HTTPS
  : process.env.VUE_APP_MULTIMEDIA_HTTP;

export default {
  name: 'ConsultaVirtual',
  components: {
    mdbBtn,
    mdbInput,
    AvisoVeracidadEspecialidad,
    modalAgendarCita,
  },
  data() {
    return {
      pageSize: 12,
      page: 1,
      filters: {
        baseSpecialty: '',
        customTag: '',
        onlyAvailableNow: true,
      },
      doctors: [],
      tags: [],
      showAgendarCita: false,
      doctorSeleccionado: {},
      debounceTimer: null,
    };
  },
  computed: {
    isLoading() {
      return !!(this.$apolloData?.queries?.doctors?.loading);
    },
  },
  methods: {
    onSearchChange() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.page = 1;
        this.$apollo.queries.doctors.refetch();
      }, 350);
    },
    limpiarFiltros() {
      this.filters.baseSpecialty = '';
      this.filters.customTag = '';
      this.filters.onlyAvailableNow = false;
      this.page = 1;
      this.$apollo.queries.doctors.refetch();
    },
    formatDoctorName(doc) {
      const parts = [
        doc.prename,
        doc.fname,
        doc.mname,
        doc.lname,
        doc.llname,
        doc.posname,
      ].filter(Boolean);
      return parts.join(' ');
    },
    getDoctorImage(doc) {
      if (doc.imageUrl) {
        return `${imgPath}dir/${doc.imageUrl}`;
      }
      if (doc.avatar) {
        return `/img/avatares/${doc.avatar}.svg`;
      }
      return '/img/vectores/AMII_img_temp.svg';
    },
    agendar(doc) {
      this.doctorSeleccionado = {
        id: doc.id,
        profileUrl: doc.profileUrl,
      };
      this.showAgendarCita = true;
    },
    cargarMas() {
      this.page++;
      this.$apollo.queries.doctors.fetchMore({
        variables: {
          baseSpecialty: this.filters.baseSpecialty || null,
          customTag: this.filters.customTag || null,
          onlyAvailableNow: this.filters.onlyAvailableNow,
          first: this.pageSize,
          offset: (this.page - 1) * this.pageSize,
        },
        updateQuery: (previousResult, {fetchMoreResult}) => {
          if (!fetchMoreResult) return previousResult;
          return {
            __typename: previousResult.activeVirtualDoctors.__typename,
            activeVirtualDoctors: [
              ...previousResult.activeVirtualDoctors,
              ...fetchMoreResult.activeVirtualDoctors,
            ],
          };
        },
      });
    },
  },
  apollo: {
    tags() {
      return {
        query: gql`${especialidadesGql}`,
      };
    },
    doctors() {
      return {
        query: gql`${activeVirtualDoctorsGql}`,
        variables() {
          return {
            baseSpecialty: this.filters.baseSpecialty || null,
            customTag: this.filters.customTag || null,
            onlyAvailableNow: this.filters.onlyAvailableNow,
            first: this.pageSize,
            offset: 0,
          };
        },
        update: (data) => data.activeVirtualDoctors || [],
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.consulta-virtual-page {
  min-height: 100vh;
  padding-top: 64px;
  overflow-x: hidden;
}
.consulta-virtual-page main > .container,
.consulta-virtual-page .container {
  max-width: 1280px;
  width: calc(100% - 2rem);
  margin: 0 auto;
  padding-left: 1rem;
  padding-right: 1rem;
}
.virtual-hero {
  background: linear-gradient(135deg, #0b2238 0%, #00a896 100%);
}
.doctor-virtual-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.08) !important;
  }
}
.doctor-avatar {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border: 2px solid #e2e8f0;
}
.badge-primary-light {
  background-color: #e6f7f5;
  color: #00a896;
}
.profile-footer {
  background: transparent !important;
}
.profile-link-wrapper {
  background: transparent !important;
  border-radius: 0;
  display: block;
  width: auto;
  text-align: center;
}
.ver-perfil-link {
  background: transparent !important;
  color: #0d6efd !important;
  text-decoration: underline !important;
  display: inline-block;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.8;
  }
}
</style>
